import type { ViewState } from './graphModel';

/** How the node set changed between two snapshots of the graph. */
export type GraphChange = { added: number; removed: number };

/** The count of node ids only in `nextIds` (added) and only in `prevIds` (removed). */
export function graphDiff(prevIds: ReadonlySet<string>, nextIds: ReadonlySet<string>): GraphChange {
  const added = [...nextIds].filter((id) => !prevIds.has(id)).length;
  const removed = [...prevIds].filter((id) => !nextIds.has(id)).length;
  return { added, removed };
}

const GROUP_PREFIX = 'group:';
const PROJECT_PREFIX = 'project:';
const namesNode = (id: string, nodeIds: ReadonlySet<string>) => nodeIds.has(id) || id.startsWith(PROJECT_PREFIX);

/**
 * The view id still names something in a graph of `nodeIds`: one of its nodes, the project root, or a group
 * (`group:<parentId>|<key>`) whose parent is one. A parent id can hold a `|` itself (a part's does), so every split
 * point is tried.
 */
export function keepsId(id: string, nodeIds: ReadonlySet<string>): boolean {
  if (namesNode(id, nodeIds)) return true;
  if (!id.startsWith(GROUP_PREFIX)) return false;
  const parts = id.slice(GROUP_PREFIX.length).split('|');
  return parts.slice(0, -1).some((_, i) => namesNode(parts.slice(0, i + 1).join('|'), nodeIds));
}

/**
 * The view state without the expanded, revealed, and paged ids that no longer name anything in a graph of
 * `nodeIds` (see `keepsId`). Each part that loses nothing is returned as it was, and so is the state.
 */
export function pruneViewState(state: ViewState, nodeIds: ReadonlySet<string>): ViewState {
  const keep = (id: string) => keepsId(id, nodeIds);
  const expanded = [...state.expandedIds].filter(keep);
  const revealed = [...state.revealedIds].filter(keep);
  const pages = [...state.pageByGroup].filter(([id]) => keep(id));
  const expandedIds = expanded.length === state.expandedIds.size ? state.expandedIds : new Set(expanded);
  const revealedIds = revealed.length === state.revealedIds.size ? state.revealedIds : new Set(revealed);
  const pageByGroup = pages.length === state.pageByGroup.size ? state.pageByGroup : new Map(pages);
  if (expandedIds === state.expandedIds && revealedIds === state.revealedIds && pageByGroup === state.pageByGroup)
    return state;
  return { ...state, expandedIds, revealedIds, pageByGroup };
}
